import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Timestamp } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { useGlobalActivityTimers } from '../hooks/useGlobalActivityTimers';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { updateMemberTotalPoints } from '../services/pointsService';
import {
  createActiveTimer,
  updateActiveTimer,
  deleteActiveTimer,
  pauseTimer,
  resumeTimer,
  findExistingTimer,
  calculateTimerState,
} from '../services/activeTimersService';
import type { ActiveTimer, CreateActiveTimer } from '../types/activeTimer';
import { getLocalDateKey } from '../utils/dateKey';

interface GlobalTimersContextValue {
  timers: ActiveTimer[];
  loading: boolean;
  nowTick: number;
  
  // Acciones
  startTimer: (data: CreateActiveTimer) => Promise<string>;
  pauseTimerById: (timerId: string) => Promise<void>;
  resumeTimerById: (timerId: string) => Promise<void>;
  stopTimer: (timerId: string) => Promise<void>;
  completeTimerById: (timerId: string, opts?: { force?: boolean; reason?: 'complete' | 'terminate' | 'day_change' }) => Promise<void>;
  getTimerState: (timer: ActiveTimer) => ReturnType<typeof calculateTimerState>;
  
  // Helpers
  getMemberTimers: (memberId: string) => ActiveTimer[];
  getActivityTimer: (memberId: string, activityId: string, startedPeriod?: string) => ActiveTimer | null;
}

const GlobalTimersContext = createContext<GlobalTimersContextValue | undefined>(undefined);

export function GlobalTimersProvider({ 
  children,
  familyId,
}: { 
  children: React.ReactNode;
  familyId: string | null;
}) {
  const { timers, loading } = useGlobalActivityTimers(familyId);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const wakeLockRef = useRef<any>(null);
  const lastCleanupDateRef = useRef<string>(getLocalDateKey());
  const completingTimerIdsRef = useRef<Set<string>>(new Set());

  const getTimersFromPreviousDays = useCallback((today: string) => {
    return timers.filter(t => {
      try {
        const timerDate = getLocalDateKey(t.createdAt.toDate());
        return timerDate !== today;
      } catch {
        return false;
      }
    });
  }, [timers]);

  const completeTimerById = useCallback(async (
    timerId: string,
    opts?: { force?: boolean; reason?: 'complete' | 'terminate' | 'day_change' }
  ) => {
    if (completingTimerIdsRef.current.has(timerId)) return;
    const timer = timers.find(t => t.id === timerId);
    if (!timer) return;

    completingTimerIdsRef.current.add(timerId);

    const state = calculateTimerState(timer, Date.now());
    const elapsedMinutes = state.totalElapsedSec / 60;
    const durationMinutes = Number(timer.durationMinutes) || 0;
    const graceMinutes = Number(timer.graceMinutes) || 0;
    const basePts = Number(timer.rewardPoints) || 0;

    const completionGoal = timer.completionGoal || 'before_time';
    const penaltyMode = timer.latePenaltyMode || 'full';

    let pointsToApply = basePts;
    let phase: 'green' | 'yellow' | 'red' = 'green';

    if (elapsedMinutes <= durationMinutes) phase = 'green';
    else if (elapsedMinutes <= durationMinutes + graceMinutes) phase = 'yellow';
    else phase = 'red';

    if (completionGoal === 'before_time') {
      if (elapsedMinutes > durationMinutes + graceMinutes) {
        if (penaltyMode === 'full') pointsToApply = -basePts;
        else if (penaltyMode === 'half') pointsToApply = -Math.floor(basePts / 2);
        else if (penaltyMode === 'none') pointsToApply = basePts;
      }
    } else if (completionGoal === 'complete_time') {
      if (elapsedMinutes < durationMinutes && durationMinutes > 0) {
        if (penaltyMode === 'penalty') pointsToApply = -basePts;
        else if (penaltyMode === 'full') pointsToApply = 0;
        else if (penaltyMode === 'half') pointsToApply = Math.floor(basePts / 2);
        else if (penaltyMode === 'none') pointsToApply = basePts;
      }
    }

    const dateKey = getLocalDateKey();

    const timeOfDayArray = Array.isArray(timer.timeOfDay) ? timer.timeOfDay : [];
    const completedPeriod = timeOfDayArray.length > 0 ? (timer.startedPeriod || null) : null;
    const completedKey = completedPeriod ? `${timer.activityId}_${completedPeriod}` : timer.activityId;

    try {
      // 1) Escribir daily_log
      await addDoc(collection(db, 'daily_log'), {
        type: 'activity',
        activityId: timer.activityId,
        memberId: timer.memberId,
        familyId: timer.familyId,
        points: Number(pointsToApply) || 0,
        status: 'completed',
        completed: true,
        date: dateKey,
        createdAt: serverTimestamp(),
        memberName: timer.memberName,
        memberAvatar: timer.memberAvatar,
        activityName: timer.activityName,
        activityIcon: timer.activityIcon,
        completedAt: serverTimestamp(),
        elapsedMinutes: Math.round(elapsedMinutes * 10) / 10,
        phase,
        completionGoal,
        completionReason: opts?.reason || 'complete',
        timeOfDay: timeOfDayArray,
        ...(completedPeriod ? { completedPeriod } : {})
      });

      try {
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('activity_timer_completed', {
            detail: {
              familyId: timer.familyId,
              memberId: timer.memberId,
              dateKey,
              completedKey,
              timerId: timer.id,
              activityId: timer.activityId,
              completedPeriod,
              reason: opts?.reason || 'complete',
            }
          }));
        }
      } catch {
        // ignore event dispatch failures
      }

      // 2) TotalPoints del miembro (fuente general)
      await updateMemberTotalPoints(timer.memberId, Number(pointsToApply) || 0, 'activity');

      // 3) Solo si todo salió OK, cerramos el timer
      await deleteActiveTimer(timer.id);
    } catch (e) {
      console.error('completeTimerById failed; keeping timer active', e);
      throw e;
    } finally {
      completingTimerIdsRef.current.delete(timerId);
    }
  }, [timers]);

  // ✅ CLEANUP: Delete paused timers from previous days (daily reset)
  useEffect(() => {
    if (timers.length === 0) return;
    
    const today = getLocalDateKey();
    if (lastCleanupDateRef.current === today) return;
    
    lastCleanupDateRef.current = today;
    
    // Eliminar/completar timers de días anteriores
    const oldTimers = getTimersFromPreviousDays(today);
    oldTimers.forEach(async (timer) => {
      try {
        if (timer.completionGoal === 'complete_time') return;
        if (timer.running) {
          await completeTimerById(timer.id, { reason: 'day_change' });
        } else {
          await deleteActiveTimer(timer.id);
        }
      } catch {}
    });
  }, [timers, getTimersFromPreviousDays, completeTimerById]);
  
  // ✅ CLEANUP: On visibility change (app return), check for day change
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      
      const today = getLocalDateKey();
      if (lastCleanupDateRef.current === today) return;
      
      lastCleanupDateRef.current = today;
      
      // Force cleanup immediately
      const oldTimers = getTimersFromPreviousDays(today);
      oldTimers.forEach(async (timer) => {
        try {
          if (timer.completionGoal === 'complete_time') return;
          if (timer.running) {
            await completeTimerById(timer.id, { reason: 'day_change' });
          } else {
            await deleteActiveTimer(timer.id);
          }
        } catch {}
      });
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [getTimersFromPreviousDays, completeTimerById]);

  // ✅ CLEANUP: While app is open, periodically detect day change (midnight) and cleanup paused timers
  useEffect(() => {
    if (timers.length === 0) return;

    const checkDayChangeAndCleanup = () => {
      const today = getLocalDateKey();
      if (lastCleanupDateRef.current === today) return;

      lastCleanupDateRef.current = today;
      const oldTimers = getTimersFromPreviousDays(today);
      oldTimers.forEach(async (timer) => {
        try {
          if (timer.completionGoal === 'complete_time') return;
          if (timer.running) {
            await completeTimerById(timer.id, { reason: 'day_change' });
          } else {
            await deleteActiveTimer(timer.id);
          }
        } catch {}
      });
    };

    // Run once in case the provider mounted after midnight
    checkDayChangeAndCleanup();

    const interval = setInterval(checkDayChangeAndCleanup, 60 * 1000);
    return () => clearInterval(interval);
  }, [timers.length, getTimersFromPreviousDays, completeTimerById]);

  // Tick cada segundo si hay timers corriendo
  React.useEffect(() => {
    const hasRunning = timers.some(t => t.running);
    if (!hasRunning) return;

    const interval = setInterval(() => {
      setNowTick(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [timers]);

  // Mantener pantalla activa mientras haya timers corriendo
  useEffect(() => {
    const hasRunning = timers.some(t => t.running);

    const requestWakeLock = async () => {
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'android' || platform === 'ios';

      if (isNative) {
        try {
          const mod = await import('@capacitor-community/keep-awake');
          await mod.KeepAwake.keepAwake();
        } catch {
          // Plugin no instalado o no disponible
        }
        return;
      }

      try {
        const navAny = navigator as any;
        if (navAny?.wakeLock?.request) {
          wakeLockRef.current = await navAny.wakeLock.request('screen');
        }
      } catch {
        // Wake Lock API no disponible o request falló
      }
    };

    const releaseWakeLock = async () => {
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'android' || platform === 'ios';

      if (isNative) {
        try {
          const mod = await import('@capacitor-community/keep-awake');
          await mod.KeepAwake.allowSleep();
        } catch {
          // Plugin no instalado o no disponible
        }
        return;
      }

      try {
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
        }
      } catch {
        wakeLockRef.current = null;
      }
    };

    if (hasRunning) {
      void requestWakeLock();
    } else {
      void releaseWakeLock();
    }

    return () => {
      void releaseWakeLock();
    };
  }, [timers]);

  // Iniciar un nuevo timer
  const startTimer = useCallback(async (data: CreateActiveTimer): Promise<string> => {
    const timeOfDayArray = Array.isArray((data as any)?.timeOfDay) ? (data as any).timeOfDay : [];
    const shouldUsePeriod = timeOfDayArray.length > 0;
    const normalizedStartedPeriod: string | undefined = shouldUsePeriod
      ? (data.startedPeriod || undefined)
      : undefined;

    // Verificar si ya existe un timer para esta actividad
    const existing = await findExistingTimer(
      data.familyId,
      data.memberId,
      data.activityId,
      normalizedStartedPeriod
    );
    
    if (existing) {
      // Si existe pero está pausado, reanudarlo
      if (!existing.running) {
        await resumeTimer(existing);
        try {
          setNowTick(Date.now());
        } catch {
        }
        return existing.id;
      }
      // Si ya está corriendo, retornar el id existente
      return existing.id;
    }

    // Crear nuevo timer
    const timerId = await createActiveTimer({
      ...data,
      startedPeriod: normalizedStartedPeriod,
      startedAt: Timestamp.now(),
      running: true,
      accumulatedSec: 0,
    });

    try {
      setNowTick(Date.now());
    } catch {
    }

    return timerId;
  }, []);

  const getMemberTimers = useCallback((memberId: string) => {
    return timers.filter(t => t.memberId === memberId);
  }, [timers]);

  const getActivityTimer = useCallback((memberId: string, activityId: string, startedPeriod?: string) => {
    const byMemberAndActivity = timers.filter(
      t => t.memberId === memberId && String(t.activityId) === String(activityId)
    );

    if (byMemberAndActivity.length === 0) return null;

    if (startedPeriod) {
      return byMemberAndActivity.find(t => String(t.startedPeriod || '') === String(startedPeriod)) || null;
    }

    // Fallback: si no se especifica período, devolver el primero (comportamiento anterior)
    return byMemberAndActivity[0] || null;
  }, [timers]);

  // Reanudar timer por ID
  const resumeTimerById = useCallback(async (timerId: string) => {
    const timer = timers.find(t => t.id === timerId);
    if (!timer) return;

    await resumeTimer(timer);

    try {
      setNowTick(Date.now());
    } catch {
    }
  }, [timers]);

  // Detener/eliminar timer
  const stopTimer = useCallback(async (timerId: string) => {
    await deleteActiveTimer(timerId);
  }, []);

  // Pausar timer por ID
  const pauseTimerById = useCallback(async (timerId: string) => {
    const timer = timers.find(t => t.id === timerId);
    if (!timer) return;

    // Si está en penalización (before_time y fase roja), al intentar pausar lo cerramos aplicando penalización
    try {
      const state = calculateTimerState(timer, Date.now());
      if ((timer.completionGoal || 'before_time') === 'before_time' && state.phase === 'red') {
        await completeTimerById(timerId, { reason: 'terminate' });
        return;
      }
    } catch {
      // fallback a pausa normal
    }

    await pauseTimer(timer);

    try {
      setNowTick(Date.now());
    } catch {
    }
  }, [timers, completeTimerById]);

  // Obtener estado calculado del timer
  const getTimerState = useCallback((timer: ActiveTimer) => {
    // Cuando está corriendo, usar Date.now() para evitar desfases de 1s
    // (por ejemplo justo después de reanudar, antes del próximo tick).
    const t = timer.running ? Date.now() : nowTick;
    return calculateTimerState(timer, t);
  }, [nowTick]);

  const value: GlobalTimersContextValue = {
    timers,
    loading,
    nowTick,
    startTimer,
    pauseTimerById,
    resumeTimerById,
    stopTimer,
    completeTimerById,
    getTimerState,
    getMemberTimers,
    getActivityTimer,
  };

  return (
    <GlobalTimersContext.Provider value={value}>
      {children}
    </GlobalTimersContext.Provider>
  );
}

export function useGlobalTimers() {
  const context = useContext(GlobalTimersContext);
  if (context === undefined) {
    throw new Error('useGlobalTimers debe usarse dentro de GlobalTimersProvider');
  }
  return context;
}
